'use client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { useCollectableCouponLayout } from './hooks/useCollectableCouponLayout';
import { useCollectableCouponList } from './hooks/useCollectableCouponList';
import { useCollectableCouponTimer } from './hooks/useCollectableCouponTimer';
import { useCollectCouponMutation } from '@/hooks/useCollectableCoupon';
import { useOrderStore } from '@/store/order';
import { buildGrid } from './lib/grid';
import { FloatingCouponButton } from './parts/FloatingCouponButton';
import { CollectableCouponsModal } from './parts/CollectableCouponsModal';
import { CouponCard } from './parts/CouponCard';
import { CollectableCouponStyles } from './parts/CollectableCouponStyles';
import Popup from '../Popup';

const FloatingCollectableCoupon = () => {
  const pathName = usePathname();
  const collectCouponMutation = useCollectCouponMutation();
  const bopisOrders = useOrderStore((state) => state.bopisOrders);

  const layout = useCollectableCouponLayout();
  const list = useCollectableCouponList({
    pageSize: layout.pageSize,
    viewportHeight: layout.viewportHeight,
  });
  const timer = useCollectableCouponTimer(list.availableCoupons.length);

  const [modalOpen, setModalOpen] = useState(false);
  const [popupOpen, setPopupOpen] = useState(false);
  const [pageIndex, setPageIndex] = useState(0);
  const swiperRef = useRef<any>(null);

  const grid = useMemo(
    () => buildGrid(list.availableCoupons.length, layout.pageSize, layout.viewportHeight),
    [list.availableCoupons.length, layout.pageSize, layout.viewportHeight],
  );

  const safePage = Math.min(pageIndex, grid.totalPages - 1);

  const handleCollect = async (couponId?: string) => {
    const targetId = couponId ?? list.currentCoupon?.Id;
    if (!targetId) return;

    // Capture pageIndex because React state will have advanced by the
    // time the mutation resolves.
    const capturedSafePage = safePage;

    // Auth-redirect path: list hook handles state, but we also need to
    // close the modal/popup surfaces locally.
    if (!list.isAuthenticated) {
      handleClose('popup');
      handleClose('modal');
      await list.handleCollect(targetId);
      // router.push('/sign-in') happens inside list hook; do not advance.
      return;
    }

    // `availableCoupons` is the pre-collect list (state hasn't
    // re-rendered yet); subtract one for the post-collect count.
    const postCollectCount = list.availableCoupons.length - 1;
    await list.handleCollect(targetId);

    // Modal stays open. Advance the swiper to the next page that has
    // coupons, or close the modal when the list is empty.
    const nextPage = list.computeNextPage(targetId, capturedSafePage);
    if (nextPage === null) {
      if (postCollectCount === 1) {
        // One coupon left — same as FAB click for N=1: close modal,
        // open popup with the remaining coupon.
        handleClose('modal');
        list.setCurrentCoupon(list.availableCoupons.find((c) => c.Id !== targetId)!);
        setPopupOpen(true);
        return;
      }
      if (postCollectCount === 0) {
        handleClose('modal');
        timer.hideFloatingButton();
      }
      // If nextPage is null but more coupons remain, the swiper will
      // visually shift on re-render without needing slideTo.
      return;
    }
    setPageIndex(nextPage);
    swiperRef.current?.slideToLoop(nextPage);
  };

  const handleClose = (type?: 'modal' | 'popup') => {
    if (!type || type === 'modal') setModalOpen(false);
    if (!type || type === 'popup') setPopupOpen(false);
    if (type === 'popup') list.setCurrentCoupon(null);
  };

  // Auto-hide when the list empties after a collect.
  useEffect(() => {
    if (list.displayedCoupons.length === 0 && list.collectedCouponIds.length > 0) {
      timer.hideFloatingButton();
    }
  }, [list.displayedCoupons.length, list.collectedCouponIds.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hide the FAB on /sign-in.
  const isSignIn = pathName?.includes('/sign-in') ?? false;

  // Skip all surfaces on /checkout.
  if (pathName?.includes('/checkout')) return null;
  if (list.displayedCoupons.length === 0) return null;

  const isOneCardLayout = layout.pageSize === 1;

  return (
    <>
      {timer.showFloatingButton && !isSignIn && (
        <FloatingCouponButton
          count={list.availableCoupons.length}
          hasBopisOrders={Boolean(bopisOrders && bopisOrders.length > 0)}
          iconUrl={timer.settings?.IconUrl}
          onClick={() => {
            if (list.availableCoupons.length === 1) {
              list.setCurrentCoupon(list.availableCoupons[0]);
              setPopupOpen(true);
            } else {
              setModalOpen(true);
            }
          }}
        />
      )}

      <CollectableCouponsModal
        open={modalOpen}
        onClose={() => handleClose('modal')}
        coupons={list.availableCoupons}
        isOneCardLayout={isOneCardLayout}
        grid={grid}
        pageSize={layout.pageSize}
        pageIndex={safePage}
        onPageChange={setPageIndex}
        swiperRef={swiperRef}
        onCollect={handleCollect}
        isCollecting={collectCouponMutation.isPending}
        collectingId={typeof collectCouponMutation.variables === 'string' ? collectCouponMutation.variables : undefined}
      />

      <Popup open={popupOpen} closePopup={() => handleClose('popup')} removeShadow>
        <CouponCard
          coupon={list.currentCoupon ?? list.availableCoupons[0]}
          onCollect={(id) => handleCollect(id)}
          isCollecting={collectCouponMutation.isPending}
          collectingId={typeof collectCouponMutation.variables === 'string' ? collectCouponMutation.variables : undefined}
          className="mx-auto"
        />
      </Popup>

      <CollectableCouponStyles />
    </>
  );
};

export default FloatingCollectableCoupon;